import { produce } from "immer";
import * as Actions from "pages/Mail/Messages/store/messageActions";
import * as singleMessageActions from "pages/Mail/ReadMessage/store/singleMessageActions";
import * as conversationActions from "pages/Mail/ReadMessage/store/conversationActions";
import * as reminderActions from "pages/Mail/Messages/store/reminderActions";

import deepmerge from "deepmerge";
import combineMerge from "config/combineMerge";
import idFixer, { isConversationId } from "utils/idFixer";
import { applyReminderFields } from "pages/Mail/helpers/reminders";

const mergeById = (current, incoming) => {
  const merged = deepmerge(current, incoming, {
    arrayMerge: combineMerge
  });

  Object.keys(incoming).forEach(id => {
    applyReminderFields(merged[id], incoming[id]);
  });

  return merged;
};

const initialState = { byId: {}, allIds: [], hasMore: true };
const messageReducer = produce((draft, action) => {
  switch (action.type) {
    case Actions.FETCH_MESSAGES_SUCCESS: {
      const payload = action.payload;
      const messages = payload.messages;
      const listingType = payload.listingType;
      const messageIds = payload.messages.map(message => idFixer(message.id));

      const byId = messages.reduce((obj, message) => {
        message = {
          ...message,
          listingType:
            listingType === "conversation" && isConversationId(message.id)
              ? "conversation"
              : "message",
          id: idFixer(message.id)
        };
        obj[message.id] = message;
        return obj;
      }, {});

      draft.byId = mergeById(draft.byId, byId);
      draft.allIds = messageIds;
      draft.hasMore = messageIds.length >= (payload.perPage || 20);

      break;
    }

    case Actions.FETCH_MESSAGES_APPEND_SUCCESS: {
      const payload = action.payload;
      const messages = payload.messages;
      const listingType = payload.listingType;
      const messageIds = payload.messages.map(message => idFixer(message.id));

      const byId = messages.reduce((obj, message) => {
        message = {
          ...message,
          listingType:
            listingType === "conversation" && isConversationId(message.id)
              ? "conversation"
              : "message",
          id: idFixer(message.id)
        };
        obj[message.id] = message;
        return obj;
      }, {});

      draft.byId = mergeById(draft.byId, byId);
      const newIds = messageIds.filter(id => !draft.allIds.includes(id));
      draft.allIds = [...draft.allIds, ...newIds];
      draft.hasMore = messageIds.length >= (payload.perPage || 20);

      break;
    }

    case conversationActions.FETCH_SUB_CONVERSATIONS_SUCCESS: {
      const payload = action.payload;
      const id = idFixer(payload.id);
      const children = payload.children;
      const childIds = children.map(message => idFixer(message.id));

      const childrenWithout = children.filter(item => {
        return idFixer(item.id) !== id;
      });

      const byId = childrenWithout.reduce((obj, message) => {
        message = {
          ...message,
          listingType: "message",
          id: idFixer(message.id)
        };
        obj[message.id] = message;
        return obj;
      }, {});

      draft.byId = mergeById(draft.byId, byId);

      draft.byId[id] = {
        ...draft.byId[id],
        child_content: children.find(item => idFixer(item.id) === id),
        id,
        children: childIds
      };

      break;
    }

    case conversationActions.FETCH_CONVERSATION_SUCCESS: {
      const payload = action.payload;
      const id = idFixer(payload.id);
      const conversations = payload.conversations;
      const conversationIds = conversations.map(message => idFixer(message.id));

      const allIds = [id];
      const byId = {
        [id]: {
          id,
          conversations: conversationIds
        }
      };

      draft.byId = deepmerge(byId, draft.byId, {
        arrayMerge: combineMerge
      });

      draft.allIds = deepmerge(draft.allIds, allIds, {
        arrayMerge: combineMerge
      });

      break;
    }

    case singleMessageActions.FETCH_SINGLE_MESSAGE_SUCCESS: {
      const payload = action.payload;
      const id = idFixer(payload.id);
      const singleMessage = payload.singleMessage;

      const byId = {
        [id]: {
          id,
          singleMessage: singleMessage.id
        }
      };

      draft.byId = mergeById(draft.byId, byId);

      break;
    }

    case Actions.UNREAD_MESSAGES: {
      const payload = action.payload;

      payload.allIds.forEach(id => {
        if (id in draft.byId) {
          draft.byId[id].unread = true;
        }
      });

      break;
    }

    case Actions.READ_MESSAGES: {
      const payload = action.payload;

      payload.allIds.forEach(id => {
        if (id in draft.byId) {
          draft.byId[id].unread = false;

          const conversationId =
            draft.byId[id].conversation_id ||
            draft.byId[id].conversation_id_other;

          if (conversationId) {
            const conversation = Object.keys(draft.byId).find(id => {
              const item = draft.byId[id];
              const conId = item.conversation_id || item.conversation_id_other;
              return conId === conversationId;
            });
            if (conversation) {
              if (conversation === id) {
                return;
              }
              /* if all messages in conversations array are read, 
              /  mark conversation as read
              */
              const conversations =
                draft.byId[conversation]?.children ||
                draft.byId[conversation]?.conversations;

              if (conversations) {
                const unread = conversations?.find(id => {
                  if (id === conversation) return false;
                  return draft.byId[id]?.unread;
                });

                if (!unread) {
                  draft.byId[conversation].unread = false;
                }
              }
            }
          }
        }
      });

      break;
    }

    case Actions.TOGGLE_FLAG: {
      const payload = action.payload;

      payload.allIds.forEach(id => {
        draft.byId[id].flagged = !payload.flagged;
      });

      break;
    }

    case reminderActions.SET_MESSAGE_REMINDERS: {
      const { id, ...fields } = action.payload;
      const item = draft.byId[id];

      if (!item) {
        break;
      }

      if (item.listingType === "conversation") {
        if (item.child_content && idFixer(item.child_content.id) === id) {
          applyReminderFields(item.child_content, fields);
        }
        break;
      }

      applyReminderFields(item, fields);
      break;
    }

    case reminderActions.SET_CONVERSATION_REMINDER: {
      const { id, has_reminder } = action.payload;

      if (draft.byId[id]?.listingType === "conversation") {
        draft.byId[id].has_reminder = has_reminder;
      }
      break;
    }

    case Actions.PREPEND_MESSAGE: {
      const { payload } = action;
      let {
        id: m_id,
        conversation_id,
        first_message_id,
        first_line,
        date,
        subject,
        unread,
        folder_name,
        folder_path,
        n
      } = payload;
      const id = first_message_id > 0 ? first_message_id : m_id;
      conversation_id = conversation_id && conversation_id;
      let data = payload;
      if (first_message_id > 0 && draft.byId[id]) {
        data = draft.byId[id];
        data.subject = subject;
        data.first_line = first_line;
        data.date = date;
        data.unread = unread;
        data.folder_name = folder_name;
        data.folder_path = folder_path;
        data.n = n;
        const convers = data.conversations;
        const newArray = [m_id].concat(convers);
        data.conversations = newArray;
      }
      draft.byId[id] = deepmerge(draft.byId[id], data, {
        arrayMerge: combineMerge
      });

      if (conversation_id) {
        // remove old message
        const messages = draft.allIds.map(id => {
          return draft.byId[id];
        });
        const old = messages.find(message => {
          return message.conversation_id === conversation_id;
        });

        if (old) {
          draft.allIds = draft.allIds.filter(id => id !== old.id);
        }
      }
      if (first_message_id < 1) {
        draft.allIds.unshift(m_id);
      }
      break;
    }
    default:
      return draft;
  }
}, initialState);

export default messageReducer;
